import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import {
  Upload,
  Download,
  Trash2,
  FileText,
  Image as ImageIcon,
  File as FileIcon,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
} from "lucide-react";

const ALL_YEARS = "__all__";
const ALL_CATEGORIES = "__all__";

const formatBytes = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fileIcon = (mime) => {
  if (!mime) return <FileIcon className="h-4 w-4 text-slate-500" />;
  if (mime === "application/pdf")
    return <FileText className="h-4 w-4 text-red-600" />;
  if (mime.startsWith("image/"))
    return <ImageIcon className="h-4 w-4 text-emerald-600" />;
  if (mime.includes("word"))
    return <FileText className="h-4 w-4 text-blue-600" />;
  if (mime.includes("sheet") || mime.includes("excel"))
    return <FileText className="h-4 w-4 text-emerald-700" />;
  if (mime.includes("presentation") || mime.includes("powerpoint"))
    return <FileText className="h-4 w-4 text-orange-600" />;
  return <FileIcon className="h-4 w-4 text-slate-500" />;
};

const statusBadge = (s) => {
  if (s === "approved")
    return (
      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-xs">
        <CheckCircle2 className="h-3 w-3 mr-1" /> Approved
      </Badge>
    );
  if (s === "rejected")
    return (
      <Badge className="bg-red-100 text-red-800 border-red-200 text-xs">
        <XCircle className="h-3 w-3 mr-1" /> Rejected
      </Badge>
    );
  return (
    <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
      <Clock className="h-3 w-3 mr-1" /> Pending
    </Badge>
  );
};

export const DocumentRepositoryDialog = ({ isOpen, onClose, currentUser }) => {
  const isAdmin = currentUser?.role === "Administrator";
  const [tab, setTab] = useState("all");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState(ALL_CATEGORIES);
  const [filterYear, setFilterYear] = useState(ALL_YEARS);

  // Upload form
  const [uploadOpen, setUploadOpen] = useState(false);
  const [upForm, setUpForm] = useState({
    title: "",
    description: "",
    category: "",
    year: String(new Date().getFullYear()),
    file: null,
  });
  const [uploading, setUploading] = useState(false);

  // Rejection prompt
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === "mine") params.mine = true;
      else if (tab === "pending" && isAdmin) params.status = "pending";
      if (filterCategory !== ALL_CATEGORIES) params.category = filterCategory;
      if (filterYear !== ALL_YEARS) params.year = parseInt(filterYear, 10);
      const res = await axios.get("documents", { params });
      setDocs(res.data?.documents || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get("documents/categories");
      setCategories(res.data?.categories || []);
    } catch (err) {
      // Non-fatal: dialog still works, but no category dropdown options.
      // Log for diagnostics — keep the user UX silent.
      console.warn("documents/categories fetch failed:", err?.response?.status, err?.message);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setTab("all");
    setFilterCategory(ALL_CATEGORIES);
    setFilterYear(ALL_YEARS);
    fetchCategories();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    fetchDocs();
  }, [isOpen, tab, filterCategory, filterYear]);

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    const years = new Set([now, now - 1, now - 2, now - 3, now + 1]);
    docs.forEach((d) => d?.year && years.add(d.year));
    return Array.from(years).sort((a, b) => b - a);
  }, [docs]);

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`documents/${doc.id}/download`);
      const url = res.data?.download_url;
      if (!url) throw new Error("No URL");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate download link");
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`documents/${doc.id}`);
      toast.success("Document deleted");
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const handleApprove = async (doc) => {
    try {
      await axios.post(`documents/${doc.id}/approve`);
      toast.success("Document approved");
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    }
  };

  const submitReject = async () => {
    if (rejectReason.trim().length < 3) {
      toast.error("Please provide a short reason");
      return;
    }
    try {
      await axios.post(`documents/${rejectingId}/reject`, {
        reason: rejectReason.trim(),
      });
      toast.success("Document rejected");
      setRejectingId(null);
      setRejectReason("");
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    }
  };

  const submitUpload = async (e) => {
    e?.preventDefault?.();
    if (!upForm.title.trim() || !upForm.category || !upForm.file) {
      toast.error("Title, category and file are required");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", upForm.title.trim());
      fd.append("description", upForm.description.trim());
      fd.append("category", upForm.category);
      fd.append("year", upForm.year);
      fd.append("file", upForm.file);
      const res = await axios.post("documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        res.data?.status === "approved"
          ? "Document uploaded and published"
          : "Document uploaded — waiting for admin approval"
      );
      setUploadOpen(false);
      setUpForm({
        title: "",
        description: "",
        category: "",
        year: String(new Date().getFullYear()),
        file: null,
      });
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="document-repository-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-700" />
            Document Repository
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 flex-wrap mt-1">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="all" data-testid="docs-tab-all">
                All documents
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="mine" data-testid="docs-tab-mine">
                  My uploads
                </TabsTrigger>
              )}
              {isAdmin && (
                <TabsTrigger value="pending" data-testid="docs-tab-pending">
                  Pending approval
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>

          {isAdmin && (
            <Button
              type="button"
              onClick={() => setUploadOpen(true)}
              data-testid="docs-upload-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload document
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap mt-3">
          <Label className="text-xs text-slate-500">Category:</Label>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-56" data-testid="docs-filter-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CATEGORIES}>All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label className="text-xs text-slate-500 ml-2">Year:</Label>
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-32" data-testid="docs-filter-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_YEARS}>All years</SelectItem>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-y-auto mt-3 pr-1" data-testid="docs-list">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading…
            </div>
          ) : docs.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              No documents found.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
              {docs.map((doc) => {
                const ownedByMe = doc.uploaded_by === currentUser?.id;
                const canDelete = isAdmin;
                return (
                  <li
                    key={doc.id}
                    className="px-3 py-3 flex items-start gap-3"
                    data-testid={`doc-row-${doc.id}`}
                  >
                    <div className="mt-0.5">{fileIcon(doc.mime_type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800 truncate">
                          {doc.title}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {doc.category}
                        </Badge>
                        <Badge variant="outline" className="text-xs bg-slate-50">
                          {doc.year}
                        </Badge>
                        {(tab !== "all" || doc.status !== "approved") &&
                          statusBadge(doc.status)}
                      </div>
                      {doc.description && (
                        <p className="text-xs text-slate-600 mt-0.5 break-words">
                          {doc.description}
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        {doc.original_filename} · {formatBytes(doc.file_size)} ·
                        uploaded by {doc.uploaded_by_name} on{" "}
                        {new Date(doc.uploaded_at).toLocaleDateString("en-GB")}
                      </p>
                      {doc.status === "rejected" && doc.rejection_reason && (
                        <p className="text-xs text-red-600 mt-1">
                          Rejection reason: {doc.rejection_reason}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {(doc.status === "approved" || isAdmin || ownedByMe) && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(doc)}
                          title="Download"
                          data-testid={`doc-download-${doc.id}`}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {isAdmin && doc.status === "pending" && (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                            onClick={() => handleApprove(doc)}
                            title="Approve"
                            data-testid={`doc-approve-${doc.id}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="text-red-700 border-red-200 hover:bg-red-50"
                            onClick={() => {
                              setRejectingId(doc.id);
                              setRejectReason("");
                            }}
                            title="Reject"
                            data-testid={`doc-reject-${doc.id}`}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                      {canDelete && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleDelete(doc)}
                          title="Delete"
                          data-testid={`doc-delete-${doc.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>

      {/* Upload sub-dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => !o && setUploadOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="docs-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload a document</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitUpload} className="space-y-3">
            <div>
              <Label htmlFor="up-title">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="up-title"
                value={upForm.title}
                onChange={(e) =>
                  setUpForm((p) => ({ ...p, title: e.target.value }))
                }
                required
                data-testid="docs-upload-title"
              />
            </div>
            <div>
              <Label htmlFor="up-desc">Description</Label>
              <Textarea
                id="up-desc"
                rows={2}
                value={upForm.description}
                onChange={(e) =>
                  setUpForm((p) => ({ ...p, description: e.target.value }))
                }
                data-testid="docs-upload-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>
                  Category <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={upForm.category}
                  onValueChange={(v) =>
                    setUpForm((p) => ({ ...p, category: v }))
                  }
                >
                  <SelectTrigger data-testid="docs-upload-category">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>
                  Year <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={upForm.year}
                  onValueChange={(v) => setUpForm((p) => ({ ...p, year: v }))}
                >
                  <SelectTrigger data-testid="docs-upload-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const now = new Date().getFullYear();
                      const ys = [];
                      for (let y = now + 1; y >= now - 10; y--) ys.push(y);
                      return ys.map((y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="up-file">
                File <span className="text-red-500">*</span>
              </Label>
              <Input
                id="up-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                onChange={(e) =>
                  setUpForm((p) => ({ ...p, file: e.target.files?.[0] || null }))
                }
                data-testid="docs-upload-file"
              />
              <p className="text-xs text-slate-500 mt-1">
                PDF, images, Word, Excel, PowerPoint — max 20 MB.
              </p>
            </div>
            {!isAdmin && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                Your upload will be reviewed by an admin before it appears in
                the public list.
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => setUploadOpen(false)}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={uploading}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="docs-upload-submit"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" /> Upload
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject prompt */}
      <Dialog
        open={!!rejectingId}
        onOpenChange={(o) => !o && setRejectingId(null)}
      >
        <DialogContent className="max-w-sm" data-testid="docs-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject document</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="rej-reason" className="text-xs text-slate-500">
              Reason for rejection
            </Label>
            <Textarea
              id="rej-reason"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Content not appropriate / wrong category"
              data-testid="docs-reject-reason"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejectingId(null)}>
                Cancel
              </Button>
              <Button
                onClick={submitReject}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="docs-reject-submit"
              >
                Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default DocumentRepositoryDialog;
